import {ActionIcon, createStyles, Indicator, Popover} from '@mantine/core';
import {css} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useState} from 'react';

import {getTabStyles} from 'nav/navbar/NavBarStyles';
import {ITEM_SIZE_SIDE_NAVBAR} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {getBadgeText, NotificationTab} from './InAppNotificationConstants';
import NotificationPanel from './NotificationPanel';
import useInAppNotifications from './useInAppNotifications';

const useStyles = createStyles((theme) => ({
  // The bell sits in the fixed-bottom stack of the nav rail above the user avatar
  bellWrapper: {
    width: ITEM_SIZE_SIDE_NAVBAR,
    height: ITEM_SIZE_SIDE_NAVBAR,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Hover/open background comes from the shared nav-rail tab styles (getTabStyles) applied to the
  // slot wrapper, matching the other rail icons. The nucleus `transparent` ActionIcon variant tints
  // the glyph grey on hover; override it (doubled specificity to beat the variant selector) so the
  // bell stays white and transparent in every state, letting only the rail highlight show through.
  bellButton: {
    color: theme.fn.rgba(theme.white, 0.92),
    backgroundColor: 'transparent',
    '&&:hover, &&:active, &&:focus': {
      color: theme.fn.rgba(theme.white, 0.92),
      backgroundColor: 'transparent',
    },
  },
  dropdown: {
    padding: 0,
  },
  badge: {
    padding: `0 ${theme.spacing.sm}`,
  },
}));

/**
 * The notification bell in the global left nav rail: an unread-count badge over a bell button that
 * toggles the notification panel. The badge polls the unread summary continuously; the (heavier)
 * listing query only runs while the panel is open.
 */
export default function NotificationBellIcon(): JSX.Element {
  const {classes} = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationTab>(NotificationTab.ALL);

  const {
    unreadCount,
    notifications,
    isLoading,
    isError,
    hasMore,
    loadMore,
    hasNewNotifications,
    refreshHead,
    markRead,
    markAllRead,
    dismiss,
    refetch,
  } = useInAppNotifications(activeTab, {isPanelOpen: isOpen});

  // Surface any head-refresh rejection through the panel rather than as an unhandled rejection; the
  // pill's click drives this so the caught error just leaves the list as-is.
  const handleShowNew = useCallback(() => {
    refreshHead().catch(() => {
      // Swallowed: a failed refresh leaves the accumulated list in place.
    });
  }, [refreshHead]);

  const handleChange = useCallback((opened: boolean) => {
    setIsOpen(opened);
  }, []);

  // Following a body link marks its notification read. A link opened in place also closes the panel
  // so the destination isn't covered by it; one opened elsewhere (e.g. Cmd/Ctrl-click to a new tab)
  // leaves the panel open.
  const handleLinkClick = useCallback(
    (id: string, openedInPlace: boolean) => {
      markRead(id);
      if (openedInPlace) {
        setIsOpen(false);
      }
    },
    [markRead]
  );

  // Surface any refetch rejection through the panel's error state rather than as an unhandled
  // rejection; the hook already loads the listing on first open and refreshes on summary advance,
  // so this is only wired to the panel's explicit retry affordance.
  const handleRetry = useCallback(() => {
    refetch().catch(() => {
      // Swallowed: `isError` from the hook drives the panel's error UI.
    });
  }, [refetch]);

  const badgeText = getBadgeText(unreadCount);
  const bellLabel = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

  return (
    <div className={classNames(css(getTabStyles(isOpen)), classes.bellWrapper)}>
      <Popover
        opened={isOpen}
        onChange={handleChange}
        position="right-end"
        offset={12}
        shadow="xl"
        radius="lg"
        withinPortal={true}
        classNames={{dropdown: classes.dropdown}}
      >
        <Popover.Target>
          <Indicator
            label={badgeText ?? undefined}
            disabled={badgeText == null}
            color="red"
            size={22}
            offset={15}
            classNames={{indicator: classes.badge}}
          >
            <Tooltip isDisabled={isOpen} placement={FloatingPlacement.RIGHT} text="Notifications">
              <ActionIcon
                variant="transparent"
                size="lg"
                className={classes.bellButton}
                aria-label={bellLabel}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                onClick={() => {
                  setIsOpen((prev) => !prev);
                }}
                {...getTestProps({component: 'NotificationBellIcon', element: 'bell'})}
              >
                <BenchlingIcon icon="bell" />
              </ActionIcon>
            </Tooltip>
          </Indicator>
        </Popover.Target>
        <Popover.Dropdown>
          <NotificationPanel
            activeTab={activeTab}
            onTabChange={setActiveTab}
            notifications={notifications}
            unreadCount={unreadCount}
            isLoading={isLoading}
            isError={isError}
            hasMore={hasMore}
            onLoadMore={loadMore}
            hasNewNotifications={hasNewNotifications}
            onShowNew={handleShowNew}
            onLinkClick={handleLinkClick}
            onMarkAllRead={markAllRead}
            onDismiss={dismiss}
            onRetry={handleRetry}
          />
        </Popover.Dropdown>
      </Popover>
    </div>
  );
}
